/* eslint-disable no-unused-vars */
import React, { useState, useRef, useCallback } from "react";
import { T, CC_COLORS, CC_ICONS } from "../../lib/globals";
import { HorseIcon, CattleIcon, renderInlineIcon } from "../../lib/icons";
import { compressImage } from "../../lib/formatters";

const inputStyle = {
  background:T.card2, border:`1px solid ${T.border}`, borderRadius:8,
  padding:"10px 12px", fontSize:16, lineHeight:1.35, color:T.text, width:"100%", boxSizing:"border-box", minWidth:0,
  outline:"none", fontFamily:"'DM Sans',sans-serif",
};


function Input(p){ return <input {...p} style={{...inputStyle,...p.style}}/>; }


function Select({children,...p}){ return <select {...p} style={{...inputStyle,...p.style}}>{children}</select>; }


function Textarea(p){ return <textarea {...p} style={{...inputStyle,...p.style,resize:"vertical"}}/>; }


function extractNodeText(node){
  if(node==null || typeof node === "boolean") return "";
  if(typeof node === "string" || typeof node === "number") return String(node);
  if(Array.isArray(node)) return node.map(extractNodeText).join(" ");
  if(React.isValidElement(node)) return extractNodeText(node.props?.children);
  return "";
}


function inferConfirmMessage(children, variant){
  const label = extractNodeText(children).replace(/\s+/g, " ").trim().toLowerCase();
  if(!label) return "";
  if(label.includes("arquivar")) return "Deseja realmente arquivar este item?";
  if(label.includes("resetar")) return "Deseja realmente resetar este item? Esta ação pode remover dados operacionais.";
  if(label.includes("excluir")) return "Deseja realmente excluir este registro?";
  if(label === "sair" || label.includes("sair do app") || label.includes("encerrar sessão")) return "Deseja realmente sair do app?";
  if(variant === "danger" && (label.includes("apagar") || label.includes("remover"))) return "Deseja realmente continuar com esta ação?";
  return "";
}


function Btn({children,onClick,variant="primary",size="md",disabled=false,full=false,style={},confirmMessage=""}){
  const sz={sm:{padding:"5px 12px",fontSize:12},md:{padding:"9px 18px",fontSize:13},lg:{padding:"12px 26px",fontSize:15}};
  const vr={primary:{background:T.accent,color:"#fff"},ghost:{background:"transparent",color:T.muted,border:`1px solid ${T.border}`},danger:{background:"transparent",color:T.red,border:`1px solid ${T.red}`},success:{background:"transparent",color:T.green,border:`1px solid ${T.green}`}};
  const resolvedConfirm = confirmMessage || inferConfirmMessage(children, variant);
  const handleClick = (e)=>{
    if(disabled || !onClick) return;
    if(resolvedConfirm){
      const ok = typeof window === "undefined" ? true : window.confirm(resolvedConfirm);
      if(!ok) return;
    }
    onClick(e);
  };
  return <button onClick={!disabled?handleClick:undefined} style={{border:"none",cursor:disabled?"not-allowed":"pointer",fontFamily:"'DM Sans',sans-serif",fontWeight:600,borderRadius:8,transition:"all .15s",opacity:disabled?.4:1,width:full?"100%":"auto",...sz[size],...vr[variant],...style}}>{children}</button>;
}


function Field({label,children,col}){
  return <div style={col?{gridColumn:col}:{}}>
    <div style={{fontSize:11,fontWeight:700,color:T.muted,textTransform:"uppercase",letterSpacing:.8,marginBottom:5}}>{label}</div>
    {children}
  </div>;
}


function Badge({children,color=T.accent}){
  return <span style={{display:"inline-flex",alignItems:"center",gap:4,background:`${color}22`,color,border:`1px solid ${color}44`,borderRadius:8,padding:"2px 9px",fontSize:11,fontWeight:700}}>{children}</span>;
}


function CCBadge({cc}){ const c=CC_COLORS[cc]||T.accent; const icon = cc === "Haras" ? <HorseIcon size={16} /> : cc === "Pecuária" ? <CattleIcon size={18} /> : CC_ICONS[cc]; return <Badge color={c}>{icon} {cc}</Badge>; }


const MODULE_TOP_CARD_ICON_WRAP = { width:58, height:58, minWidth:58 };
const MODULE_TOP_CARD_ICON_STYLE = { width:46, height:46, fontSize:28 };

function normalizeModuleTopCardWrapStyle(style={}){
  return {
    ...style,
    width: MODULE_TOP_CARD_ICON_WRAP.width,
    height: MODULE_TOP_CARD_ICON_WRAP.height,
    minWidth: MODULE_TOP_CARD_ICON_WRAP.minWidth,
  };
}

function normalizeModuleTopCardIconStyle(style={}){
  const hasObjectFit = Object.prototype.hasOwnProperty.call(style || {}, "objectFit");
  return {
    ...style,
    width: MODULE_TOP_CARD_ICON_STYLE.width,
    height: MODULE_TOP_CARD_ICON_STYLE.height,
    ...(hasObjectFit ? {} : { objectFit:"contain" }),
    fontSize: MODULE_TOP_CARD_ICON_STYLE.fontSize,
  };
}

function StatCard({icon,label,value,sub,color=T.accent,onClick,iconPosition="middleRight",iconWrapStyle,iconStyle,contentPadRight,cardStyle}){
  const isTopRightIcon = iconPosition==="topRight";
  const isMiddleRightIcon = iconPosition==="middleRight";
  const isRightIcon = isTopRightIcon || isMiddleRightIcon;
  const normalizedIconWrapStyle = isRightIcon ? normalizeModuleTopCardWrapStyle(iconWrapStyle) : iconWrapStyle;
  const normalizedIconStyle = isRightIcon ? normalizeModuleTopCardIconStyle(iconStyle) : iconStyle;
  const iconNode = <div style={{
    fontSize:22,
    marginBottom:isRightIcon?0:8,
    width:isRightIcon?MODULE_TOP_CARD_ICON_WRAP.width:"auto",
    height:isRightIcon?MODULE_TOP_CARD_ICON_WRAP.height:"auto",
    minWidth:isRightIcon?MODULE_TOP_CARD_ICON_WRAP.minWidth:undefined,
    borderRadius:isRightIcon?999:undefined,
    display:"inline-flex",
    alignItems:"center",
    justifyContent:"center",
    background:isRightIcon?`${color}14`:undefined,
    border:isRightIcon?`1px solid ${color}2E`:undefined,
    color:isRightIcon?color:undefined,
    lineHeight:1,
    ...normalizedIconWrapStyle,
  }}>{React.isValidElement(icon) ? React.cloneElement(icon, { style:{ ...(icon.props?.style||{}), ...(normalizedIconStyle||{}) } }) : <span style={{display:"inline-flex",alignItems:"center",justifyContent:"center",lineHeight:1,...normalizedIconStyle}}>{renderInlineIcon(icon, { size: Math.round((normalizedIconStyle?.fontSize || 22)), title: label })}</span>}</div>;
  const resolvedRightPad = isRightIcon
    ? (contentPadRight ?? Math.max(78, (Number(normalizedIconWrapStyle?.width) || Number(normalizedIconWrapStyle?.minWidth) || MODULE_TOP_CARD_ICON_WRAP.width) + 20))
    : 0;
  return <div onClick={onClick} style={{background:T.card,border:`1px solid ${T.border}`,borderRadius:12,padding:"18px 20px",cursor:onClick?"pointer":"default",transition:"all .2s",fontFamily:"'DM Sans',sans-serif",position:"relative", ...cardStyle}}
    onMouseEnter={e=>{if(onClick){e.currentTarget.style.borderColor=color;e.currentTarget.style.transform="translateY(-2px)";}}}
    onMouseLeave={e=>{if(onClick){e.currentTarget.style.borderColor=T.border;e.currentTarget.style.transform="translateY(0)";}}} >
    {isTopRightIcon
      ? <div style={{position:"absolute",top:14,right:14}}>{iconNode}</div>
      : isMiddleRightIcon
        ? <div style={{position:"absolute",top:"50%",right:14,transform:"translateY(-50%)"}}>{iconNode}</div>
        : iconNode}
    <div style={{fontFamily:"'DM Sans',sans-serif",fontSize:22,fontWeight:700,color,lineHeight:1.1,letterSpacing:"-0.01em",paddingRight:resolvedRightPad}}>{value}</div>
    <div style={{fontFamily:"'DM Sans',sans-serif",color:T.text,fontSize:13,fontWeight:600,marginTop:4,lineHeight:1.2,paddingRight:resolvedRightPad}}>{label}</div>
    {sub&&<div style={{fontFamily:"'DM Sans',sans-serif",color:T.muted,fontSize:12,marginTop:4,lineHeight:1.25,paddingRight:resolvedRightPad}}>{sub}</div>}
  </div>;
}


function InlineModal({ open, onClose, title, children, wide=false, zIndex=200, tone="default", theme }){
  if (!open) return null;
  const accents = {
    default: { border: "rgba(59,130,246,.24)", bg: "rgba(59,130,246,.06)", title: "#3b82f6" },
    warning: { border: "rgba(245,158,11,.28)", bg: "rgba(245,158,11,.08)", title: "#f59e0b" },
    success: { border: "rgba(34,197,94,.24)", bg: "rgba(34,197,94,.06)", title: "#22c55e" },
    danger: { border: "rgba(239,68,68,.24)", bg: "rgba(239,68,68,.06)", title: "#ef4444" },
  };
  const style = accents[tone] || accents.default;
  const bg = theme?.card || '#fff';
  const contentBg = theme?.card2 || theme?.card || '#fff';
  const text = theme?.text || '#0f172a';
  const muted = theme?.muted || '#64748b';
  const shadow = theme?.bg ? '0 12px 34px rgba(0,0,0,.28)' : '0 10px 30px rgba(15,23,42,.08)';
  return (
    <div style={{marginTop:18,background:bg,border:`1px solid ${style.border}`,borderRadius:16,overflow:'hidden',boxShadow:shadow, position:'relative', zIndex, width:'100%', maxWidth:wide?1100:860}}>
      <div style={{display:'flex',justifyContent:'space-between',alignItems:'center',gap:12,padding:'14px 16px',background:contentBg,borderBottom:`1px solid ${style.border}`}}>
        <div style={{fontWeight:800,color:style.title}}>{title}</div>
        <button type="button" onClick={onClose} style={{border:'none',background:'transparent',cursor:'pointer',fontWeight:800,color:style.title,fontSize:14}}>Fechar</button>
      </div>
      <div style={{padding:16,display:'grid',gap:14,background:bg,color:text}}>{children}</div>
      <div style={{padding:'0 16px 14px',fontSize:11,color:muted,background:bg}} />
    </div>
  );
}


function Modal({open,onClose,title,children,wide=false,zIndex=200}){
  if(!open) return null;
  return <div onClick={onClose} style={{position:"fixed",inset:0,background:"rgba(0,0,0,.75)",zIndex,display:"flex",alignItems:"center",justifyContent:"center",padding:16,backdropFilter:"blur(4px)"}}>
    <div onClick={e=>e.stopPropagation()} style={{background:T.card,border:`1px solid ${T.border}`,borderRadius:16,padding:28,width:"100%",maxWidth:wide?820:560,maxHeight:"92vh",overflowY:"auto",position:"relative",zIndex:zIndex+1,boxShadow:"0 20px 60px rgba(0,0,0,.55)"}}>
      <div style={{display:"flex",justifyContent:"space-between",alignItems:"center",marginBottom:22}}>
        <div style={{fontFamily:"'Playfair Display',serif",fontSize:20,fontWeight:700,color:T.text}}>{title}</div>
        <button onClick={onClose} style={{background:"none",border:"none",color:T.muted,cursor:"pointer",fontSize:22,lineHeight:1}}>✕</button>
      </div>
      {children}
    </div>
  </div>;
}


function Table({cols,rows,onRow}){
  return <div style={{overflowX:"auto"}}>
    <table style={{width:"100%",borderCollapse:"collapse",fontSize:13}}>
      <thead><tr style={{borderBottom:`1px solid ${T.border}`}}>
        {cols.map(c=><th key={c.key} style={{padding:"10px 14px",textAlign:"left",color:T.muted,fontSize:11,fontWeight:700,textTransform:"uppercase",letterSpacing:.8}}>{c.label}</th>)}
      </tr></thead>
      <tbody>{rows.map((row,i)=>(
        <tr key={i} onClick={()=>onRow&&onRow(row)} style={{borderBottom:`1px solid ${T.border}22`,cursor:onRow?"pointer":"default",transition:"background .12s"}}
          onMouseEnter={e=>e.currentTarget.style.background=T.card2}
          onMouseLeave={e=>e.currentTarget.style.background="transparent"}>
          {cols.map(c=><td key={c.key} style={{padding:"12px 14px",color:T.text}}>{c.render?c.render(row):row[c.key]}</td>)}
        </tr>
      ))}</tbody>
    </table>
    {rows.length===0&&<div style={{padding:32,textAlign:"center",color:T.muted}}>Nenhum registro</div>}
  </div>;
}


function _PhotoButton({photo,onChange}){
  const ref=useRef(); const[lb,setLb]=useState(false);
  async function hf(e){const f=e.target.files[0];if(!f)return;onChange(await compressImage(f));e.target.value="";}
  if(photo) return <>
    <div style={{display:"flex",alignItems:"center",gap:6}}>
      <img src={photo} alt="" onClick={()=>setLb(true)} style={{width:44,height:44,objectFit:"cover",borderRadius:8,border:`2px solid ${T.border}`,cursor:"zoom-in",flexShrink:0}}/>
      <div style={{display:"flex",flexDirection:"column",gap:3}}>
        <button onClick={()=>ref.current.click()} style={{background:"none",border:`1px solid ${T.border}`,borderRadius:6,padding:"2px 6px",cursor:"pointer",fontSize:11,color:T.muted}}>🔄</button>
        <button onClick={()=>onChange(null)} style={{background:"none",border:`1px solid ${T.red}`,borderRadius:6,padding:"2px 6px",cursor:"pointer",fontSize:11,color:T.red}}>✕</button>
      </div>
    </div>
    <input ref={ref} type="file" accept="image/*" capture="environment" onChange={hf} style={{display:"none"}}/>
    {lb&&<div onClick={()=>setLb(false)} style={{position:"fixed",inset:0,background:"rgba(0,0,0,.9)",zIndex:500,display:"flex",alignItems:"center",justifyContent:"center",cursor:"zoom-out"}}><img src={photo} alt="" style={{maxWidth:"90vw",maxHeight:"90vh",borderRadius:12}}/></div>}
  </>;
  return <>
    <button onClick={()=>ref.current.click()} style={{background:T.card2,border:`1.5px dashed ${T.border}`,borderRadius:8,width:44,height:44,cursor:"pointer",fontSize:16,color:T.muted,display:"flex",alignItems:"center",justifyContent:"center"}}>📷</button>
    <input ref={ref} type="file" accept="image/*" capture="environment" onChange={hf} style={{display:"none"}}/>
  </>;
}


function Toast({toast}){
  if(!toast) return null;
  return <div style={{position:"fixed",bottom:24,left:"50%",transform:"translateX(-50%)",padding:"11px 22px",borderRadius:10,fontSize:14,fontWeight:600,zIndex:999,
    background:toast.type==="error"?"#3D1A1A":"#0D2B1A",color:toast.type==="error"?T.red:T.green,
    border:`1px solid ${toast.type==="error"?T.red:T.green}`,boxShadow:"0 4px 24px rgba(0,0,0,.6)",whiteSpace:"nowrap"}}>{toast.msg}</div>;
}


function useToast(){const[t,setT]=useState(null);const show=useCallback((msg,type="success")=>{setT({msg,type});setTimeout(()=>setT(null),type==="info"?2500:3000);},[]);return[t,show];}

// ═══════════════════════════════════════════════════════════════════════════════
// PURCHASES
// ═══════════════════════════════════════════════════════════════════════════════


function PwInput({ value, onChange, placeholder="Senha", id, onKeyDown }) {
  const [show, setShow] = useState(false);
  return (
    <div style={{position:"relative"}}>
      <input
        id={id}
        type={show?"text":"password"}
        value={value}
        onChange={onChange}
        onKeyDown={onKeyDown}
        placeholder={placeholder}
        style={{...inputStyle, width:"100%", paddingRight:40}}
      />
      <span
        onClick={()=>setShow(s=>!s)}
        style={{position:"absolute",right:10,top:"50%",transform:"translateY(-50%)",
          cursor:"pointer",fontSize:16,color:"#64748B",userSelect:"none"}}
      >{show?"🙈":"👁️"}</span>
    </div>
  );
}


export {
  Input, Select, Textarea,
  Btn, Field, Badge, CCBadge, StatCard, Modal, InlineModal, Table,
  _PhotoButton, Toast, useToast,
  PwInput,
  inputStyle,
};
